import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SiteFooter from "@/components/SiteFooter";
import { toast } from "sonner";
import { ArrowRight, Zap, FileText, LayoutGrid, Sparkles, Bot, User, X, Check, KeyRound, Target, Type, ListChecks, Image as ImageIcon, Eye, FileSearch, Palette, Layers, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const [params] = useSearchParams();
  const [seats, setSeats] = useState({ taken: 0, cap: 100, remaining: 100 });
  useEffect(() => {
    if (params.get("deleted") === "1") {
      toast.success("Your account has been deleted.");
    }
  }, [params]);
  useEffect(() => {
    axios.get(`${API}/founder/availability`).then((r) => setSeats(r.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen">
      <header className="glass-strong border-b border-foreground/30 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between gap-2">
          <Logo />
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Link to="/auth" data-testid="landing-login-link" className="btn-ghost">Login</Link>
            <Link to="/auth?mode=signup" data-testid="landing-signup-link" className="btn-primary !px-4 sm:!px-6">Sign up free</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-foreground/30">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-16 sm:py-24 md:py-36 relative">
          <div className="terminal-label mb-6 sm:mb-10" data-testid="landing-system-label">
            For creative professionals
          </div>
          <h1 className="serif-mod text-[2.75rem] leading-[1.02] sm:text-7xl sm:leading-[0.95] lg:text-[8.5rem] lg:leading-[0.95] tracking-[-0.04em] max-w-6xl" style={{ fontWeight: 600 }}>
            Beat the AI filter.
            <br/>
            <span className="whimsy text-primary text-[1.15em]">Land</span> <span className="whimsy text-primary text-[1.15em]">the interview</span><span className="text-primary">.</span>
          </h1>
          <p className="mt-8 sm:mt-12 max-w-2xl text-sm sm:text-base text-foreground/75 leading-relaxed">
            Your resume isn&apos;t the only thing being scanned anymore. Modern hiring software now reads your <span className="text-foreground">portfolio link</span> too, flattening case studies into bullet points and deciding whether a human should ever click. Sisyphean rewrites your resume <em>and</em> rebuilds a per-application portfolio for every listing, so the algorithm passes you through, and the human on the other side actually sees the work.
          </p>
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link to="/demo" data-testid="landing-cta-demo" className="btn-primary inline-flex items-center justify-center gap-3">
              Try the demo <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/auth?mode=signup" data-testid="landing-cta-trial" className="btn-secondary inline-flex items-center justify-center gap-3">
              Sign up free
            </Link>
            <a href="#for-creatives" data-testid="landing-cta-how" className="btn-ghost text-center sm:text-left text-xs sm:text-sm self-center">See how it works ↓</a>
          </div>

          <div className="mt-16 sm:mt-24 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { num: "01", label: "Resume mirrors the posting", text: "Pulls the exact keywords, titles, and skills from each listing, verbatim, not synonyms, to clear the screener" },
              { num: "02", label: "Portfolio re-orders for the role", text: "A new public portfolio page per application: relevant case studies on top, framing rewritten to match the brief" },
              { num: "03", label: "The voice stays yours", text: "Upload writing samples and Sisyphean learns how you sound. The algorithm passes, the human still hears you" },
            ].map((s) => (
              <div key={s.num} className="glass-tile p-6 sm:p-8">
                <div className="terminal-label mb-4">/ {s.num}</div>
                <div className="serif-mod text-xl sm:text-2xl mb-2 tracking-tight" style={{ fontWeight: 500 }}>{s.label}</div>
                <p className="text-sm text-foreground/70">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for creatives - portfolio-specific differentiator */}
      <section id="for-creatives" className="border-b border-foreground/30 relative">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-16 sm:py-20 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10 sm:mb-16">
            <div>
              <div className="terminal-label mb-4 text-primary" data-testid="landing-creative-label">Built for creatives</div>
              <h2 className="serif-mod text-3xl sm:text-5xl lg:text-6xl tracking-[-0.03em] max-w-4xl leading-[1.05]" style={{ fontWeight: 400 }}>
                Your <span className="whimsy text-primary text-[1.1em]">portfolio</span> is being scanned too.
                <br/>And no one&apos;s rewriting <em className="whimsy text-primary text-[1.1em]">that</em> for you.
              </h2>
              <p className="mt-6 sm:mt-8 max-w-3xl text-sm sm:text-base text-foreground/75 leading-relaxed">
                Generic resume tools were built for generic jobs. Designers, copywriters, art directors, photographers: your work lives in case studies, image grids, layouts, and craft. Modern hiring software knows this, and it&apos;s started scanning the portfolio link in your application before a human ever clicks it. Most tools ignore this entirely. <span className="text-foreground">Sisyphean is built around it.</span>
              </p>
            </div>
          </div>

          {/* Creative-specific failure modes */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-10 sm:mb-16">
            {[
              { icon: ImageIcon, label: "Visual work, flattened to text",
                body: "ATS parsers strip your portfolio site to plain text. Beautiful case studies become a wall of unstructured words - or get skipped entirely. Your hero image? Invisible.",
                test: "portfolio-failure-1" },
              { icon: Eye, label: "Recruiters scan before they click",
                body: "Increasingly, recruiters paste your portfolio URL into a screening tool to summarize what you do, what tools you use, and which industries you've shipped in - before they decide to look at the work itself.",
                test: "portfolio-failure-2" },
              { icon: FileSearch, label: "Project taxonomies matter more than aesthetics",
                body: "AI screeners look for the exact tools (Figma, After Effects, Cinema 4D), the exact roles (\"led brand identity\"), and the exact verticals (\"DTC consumer\", \"B2B SaaS\") your case studies cover.",
                test: "portfolio-failure-3" },
            ].map((s) => (
              <div key={s.label} className="brut-tile p-6 sm:p-7" data-testid={s.test}>
                <s.icon className="w-6 h-6 text-foreground/70 mb-5" strokeWidth={1.25} />
                <div className="serif-mod text-lg sm:text-xl mb-3 tracking-tight" style={{ fontWeight: 500 }}>{s.label}</div>
                <p className="text-sm text-foreground/65 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Sisyphean's portfolio-specific answer */}
          <div className="brut-tile p-6 sm:p-8 md:p-12" style={{ borderColor: "hsl(var(--primary))" }} data-testid="portfolio-solution-panel">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 items-start">
              <div className="lg:col-span-5">
                <div className="terminal-label mb-3 text-primary">[ THE.DIFFERENTIATOR ]</div>
                <h3 className="serif-mod text-2xl sm:text-4xl lg:text-5xl tracking-[-0.03em] mb-5 sm:mb-6 leading-[1.05]" style={{ fontWeight: 400 }}>
                  A portfolio rewritten for <span className="whimsy text-primary text-[1.1em]">every job.</span>
                </h3>
                <p className="text-sm text-foreground/75 leading-relaxed mb-6">
                  Sisyphean doesn&apos;t just tailor your resume. For every listing in your feed, it generates a per-application portfolio page that re-orders your work to match the role, rewrites each case study&apos;s framing in the posting&apos;s exact language, and exposes a clean, machine-readable structure underneath the visual layer, so both the AI <em>and</em> the human walk away convinced.
                </p>
                <Link to="/demo" data-testid="landing-creative-cta" className="btn-primary inline-flex items-center gap-3">
                  Try the demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: Layers, label: "Re-ordered to fit",
                    body: "Pieces relevant to the role rise to the top. The B2B SaaS work goes first when you apply to a B2B SaaS gig - automatically." },
                  { icon: Palette, label: "Reframed in the posting's language",
                    body: "Each case study's intro mirrors the role's vocabulary - \"brand systems\", \"motion direction\", whatever the JD calls it." },
                  { icon: FileSearch, label: "Machine-readable underneath",
                    body: "Structured metadata (tools, industries, your role, scope) so AI screeners can correctly index every project." },
                  { icon: Sparkles, label: "Shareable, beautiful, yours",
                    body: "A clean public URL that looks like something you would have built - not a generic resume site. Send it with confidence." },
                ].map((s) => (
                  <div key={s.label} className="glass-tile p-5">
                    <s.icon className="w-5 h-5 text-primary mb-3" strokeWidth={1.25} />
                    <div className="serif-mod text-base mb-2 tracking-tight" style={{ fontWeight: 500 }}>{s.label}</div>
                    <p className="text-xs text-foreground/65 leading-relaxed">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Live demo: same portfolio re-ordered for different jobs */}
          <PortfolioReorderDemo />

          <p className="mt-8 text-[10px] tracking-[0.2em] uppercase text-foreground/40 font-mono">
            * Per-application portfolios is a Sisyphean-specific feature. No other resume tool does this for creative work.
          </p>
        </div>
      </section>

      {/* AI vs Human comparison */}
      <section id="ai-filter" data-testid="ai-filter" className="border-b border-foreground/30 relative">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-16 sm:py-20 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10 sm:mb-16">
            <div>
              <div className="terminal-label mb-4" data-testid="landing-filter-label">The bouncer</div>
              <h2 className="serif-mod text-3xl sm:text-5xl lg:text-6xl tracking-[-0.03em] max-w-3xl leading-[1.05]" style={{ fontWeight: 400 }}>
                75% of resumes never reach
                <br/>
                <span className="whimsy text-primary text-[1.1em]">a human being.</span>
              </h2>
              <p className="mt-6 sm:mt-8 max-w-2xl text-sm sm:text-base text-foreground/75 leading-relaxed">
                Companies route every application through an Applicant Tracking System and, increasingly, a generative-AI screener. Before a recruiter sees your name, an algorithm has already decided whether you&apos;re &quot;a match.&quot; Most of the time, you&apos;re not. Not because you can&apos;t do the job, but because your resume didn&apos;t speak the algorithm&apos;s language.
              </p>
            </div>
          </div>

          {/* Side-by-side comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
            {/* Without Sisyphean */}
            <div className="brut-tile p-6 sm:p-8 md:p-10 relative" data-testid="without-folio-panel">
              <div className="flex items-center justify-between mb-6 sm:mb-8 gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-foreground/60" strokeWidth={1.25} />
                  <div className="terminal-label">Human-written application</div>
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-foreground/40">Without Sisyphean</div>
              </div>

              <h3 className="serif-mod text-2xl sm:text-3xl tracking-tight mb-6 sm:mb-8" style={{ fontWeight: 400 }}>
                Written for a person.
                <br/>Read by a robot.
              </h3>

              {/* Funnel: 100 → AI → 25 */}
              <div className="font-mono text-xs space-y-2 mb-8 p-5 border border-foreground/15 bg-background/40">
                <div className="flex justify-between"><span className="text-foreground/60">applications.sent</span><span>100</span></div>
                <div className="flex items-center justify-between text-foreground/40">
                  <span>↓ ats.filter()</span>
                  <Bot className="w-3 h-3" strokeWidth={1.5} />
                </div>
                <div className="flex justify-between"><span className="text-foreground/60">passed.keyword.match</span><span className="text-foreground/40">42</span></div>
                <div className="flex justify-between"><span className="text-foreground/60">passed.ai.screener</span><span className="text-foreground/40">25</span></div>
                <div className="border-t border-foreground/15 pt-2 mt-3 flex justify-between">
                  <span>seen.by.human</span>
                  <span className="text-primary">25 / 100</span>
                </div>
              </div>

              <ul className="space-y-3 text-sm">
                {[
                  "Synonyms instead of the exact phrasing the posting uses",
                  "Tables, columns, and headers ATS parsers mangle",
                  "Generic skills section, no posting-specific calibration",
                  "Clever copy that reads great, to a person who never sees it",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-foreground/65">
                    <X className="w-4 h-4 text-primary/70 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* With Sisyphean */}
            <div className="brut-tile p-6 sm:p-8 md:p-10 relative" style={{ borderColor: "hsl(var(--primary))" }} data-testid="with-folio-panel">
              <div className="flex items-center justify-between mb-6 sm:mb-8 gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.25} />
                  <div className="terminal-label text-primary">Sisyphean-tailored application</div>
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-primary">With Sisyphean</div>
              </div>

              <h3 className="serif-mod text-2xl sm:text-3xl tracking-tight mb-6 sm:mb-8" style={{ fontWeight: 400 }}>
                Written for the robot.
                <br/><span className="whimsy text-primary text-[1.1em]">Sounds like you.</span>
              </h3>

              {/* Funnel: 100 → AI → 92 */}
              <div className="font-mono text-xs space-y-2 mb-8 p-5 border border-primary/40 bg-primary/[0.04]">
                <div className="flex justify-between"><span className="text-foreground/60">applications.sent</span><span>100</span></div>
                <div className="flex items-center justify-between text-foreground/40">
                  <span>↓ ats.filter()</span>
                  <Sparkles className="w-3 h-3 text-primary" strokeWidth={1.5} />
                </div>
                <div className="flex justify-between"><span className="text-foreground/60">passed.keyword.match</span><span className="text-primary">96</span></div>
                <div className="flex justify-between"><span className="text-foreground/60">passed.ai.screener</span><span className="text-primary">92</span></div>
                <div className="border-t border-primary/30 pt-2 mt-3 flex justify-between">
                  <span>seen.by.human</span>
                  <span className="text-primary font-semibold">92 / 100</span>
                </div>
              </div>

              <ul className="space-y-3 text-sm">
                {[
                  "Mirrors the exact keywords, titles, and skills from each posting, verbatim",
                  "Single-column, ATS-safe formatting parsers can read cleanly",
                  "Posting-specific summary at the top, density-calibrated to the job",
                  "Voice stays yours. Your writing samples train how it sounds",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-foreground/80">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mb-20 text-[10px] tracking-[0.2em] uppercase text-foreground/40 font-mono">
            * Indicative. Pass-through rates vary by role, employer, and ATS vendor. Industry studies (Jobscan, Harvard Business School) report 60-88% of resumes are filtered before human review.
          </div>

          {/* What the AI looks for */}
          <div className="border-t border-foreground/15 pt-12 sm:pt-16">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-10 sm:mb-12">
              <div>
                <div className="terminal-label mb-4">Inside the algorithm</div>
                <h3 className="serif-mod text-2xl sm:text-4xl lg:text-5xl tracking-[-0.03em] max-w-3xl leading-[1.05]" style={{ fontWeight: 400 }}>
                  What the AI is actually
                  <br/><span className="whimsy text-primary text-[1.1em]">looking for.</span>
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { icon: KeyRound, label: "Verbatim keywords",
                  body: "Screeners search for the exact strings in the posting. \"UX Designer\" and \"User Experience Designer\" are not the same hit.",
                  folio: "Sisyphean mirrors the posting's exact phrasing." },
                { icon: Target, label: "Title alignment",
                  body: "Your most recent role title is weighted heavily. A small wording mismatch can bump your score below the threshold.",
                  folio: "Sisyphean tunes your latest title to match the JD without inventing experience." },
                { icon: ListChecks, label: "Required skills coverage",
                  body: "Postings list 8-15 \"must-haves.\" Missing more than two often auto-rejects the application.",
                  folio: "Sisyphean cross-references your skills and surfaces every match the posting calls out." },
                { icon: Type, label: "Parseable formatting",
                  body: "Multi-column layouts, text in images, fancy headers - ATS parsers strip or break them, losing key data.",
                  folio: "Sisyphean outputs single-column, plain-text-friendly resumes." },
              ].map((s) => (
                <div key={s.label} className="glass-tile p-6 sm:p-7 flex flex-col">
                  <s.icon className="w-6 h-6 text-foreground/70 mb-5" strokeWidth={1.25} />
                  <div className="serif-mod text-lg sm:text-xl mb-3 tracking-tight" style={{ fontWeight: 500 }}>{s.label}</div>
                  <p className="text-sm text-foreground/65 leading-relaxed mb-5 flex-1">{s.body}</p>
                  <div className="border-t border-foreground/15 pt-4 mt-auto flex items-start gap-2">
                    <Sparkles className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-xs text-foreground/80 leading-relaxed">{s.folio}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6 justify-between p-6 sm:p-8 brut-tile">
              <p className="serif-mod text-xl sm:text-2xl md:text-3xl tracking-tight max-w-2xl" style={{ fontWeight: 400 }}>
                Stop writing for the algorithm. <span className="whimsy text-primary">Let it work for you.</span>
              </p>
              <Link to="/auth?mode=signup" data-testid="landing-filter-cta" className="btn-primary inline-flex items-center gap-3 flex-shrink-0">
                Sign up free <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-b border-foreground/30 relative">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-16 sm:py-20 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10 sm:mb-16">
            <div>
              <div className="terminal-label mb-4">Process</div>
              <h2 className="serif-mod text-3xl sm:text-5xl lg:text-6xl tracking-[-0.03em] max-w-3xl leading-[1.05]" style={{ fontWeight: 400 }}>
                Set up once.
                <br/>
                <span className="whimsy text-primary text-[1.1em]">Open daily.</span>
              </h2>
            </div>
            <div className="text-right hidden md:block">
              <div className="serif-mod text-7xl text-foreground/20" style={{ fontWeight: 400 }}>04</div>
              <div className="terminal-label mt-1">steps</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {[
              { icon: FileText, num: "01", title: "Build your profile once",
                body: "Drop in your resume, paste a portfolio URL, or fill out the form. We parse what we find and flag anything we couldn't read clearly so you can fix the gaps. Optionally upload writing samples - Sisyphean learns your voice." },
              { icon: Sparkles, num: "02", title: "We read every posting",
                body: "Each morning Claude reads each job, extracts the exact keywords, titles, and required skills the AI screener is looking for, and scores how well you fit and how much you'd enjoy the role." },
              { icon: LayoutGrid, num: "03", title: "Materials are pre-optimized",
                body: "Toggle 'Optimize for AI' and your resume mirrors the posting verbatim - exact phrasing, single-column ATS-safe formatting, density-calibrated, with a job-specific summary at the top." },
              { icon: Zap, num: "04", title: "Review, edit, send",
                body: "Open a listing, scan the materials, edit anything inline, and click Send. Your portfolio link is shareable - looks like something you would have built yourself." },
            ].map((s) => (
              <div key={s.title} className="brut-tile p-6 sm:p-8 md:p-10 group">
                <div className="flex items-start justify-between mb-5 sm:mb-6">
                  <s.icon className="w-7 h-7 text-foreground/70" strokeWidth={1.25} />
                  <div className="serif-mod text-3xl sm:text-4xl text-foreground/20" style={{ fontWeight: 400 }}>{s.num}</div>
                </div>
                <h3 className="serif-mod text-2xl sm:text-3xl tracking-tight mb-3 sm:mb-4" style={{ fontWeight: 400 }}>{s.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-b border-foreground/30" id="pricing">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-16 sm:py-20 md:py-24">
          <div className="terminal-label mb-6 text-primary">[ PRICING ]</div>

          <div className="mb-10 sm:mb-14">
            <h2 className="serif-mod text-3xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.05] max-w-3xl mb-4" style={{ fontWeight: 500 }}>
              Sign up free. <span className="whimsy text-primary">Pay only if you stay.</span>
            </h2>
            <p className="text-sm sm:text-base text-foreground/70 max-w-2xl leading-relaxed">
              Build your profile, tailor your first three applications, and judge the work for yourself - all without a card on file. Upgrade when you&apos;re ready for the full feed.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            {/* Founders tier - dominant, live */}
            <div className="lg:col-span-7 panel-glass p-6 sm:p-8 md:p-12 relative corner-marks">
              <div className="text-xs text-foreground/60 mb-2" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>$ sisyphean --plan founders</div>
              <div className="text-xs mb-8 sm:mb-10 inline-flex items-center gap-2" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'hsl(var(--accent-green))' }}>
                <span className="blink-caret">→</span> LIMITED TO THE FIRST {seats.cap} USERS
                <span className="text-foreground/60 ml-2" data-testid="landing-founder-counter">
                  ({seats.taken}/{seats.cap} claimed{seats.remaining > 0 ? ` · ${seats.remaining} left` : " · sold out"})
                </span>
              </div>

              <div className="flex items-baseline gap-3 mb-2">
                <h2 className="serif-mod text-7xl sm:text-8xl lg:text-9xl tracking-[-0.04em] leading-none" style={{ fontWeight: 400 }}>
                  $200<span className="whimsy text-primary text-[0.5em]">.</span>
                </h2>
                <span className="text-[11px] tracking-[0.22em] uppercase text-foreground/55 font-mono pb-3">one-time</span>
              </div>
              <p className="serif-mod text-xl sm:text-2xl tracking-tight mb-6 leading-snug" style={{ fontWeight: 400 }}>
                Lifetime access. <span className="whimsy text-primary">Forever.</span>
              </p>
              <p className="text-sm sm:text-base text-foreground/75 max-w-xl mb-8 sm:mb-10 leading-relaxed">
                We&apos;re looking for {seats.cap} founding users to help us build Sisyphean right. Pay $200 once. Get everything we ever release. No subscription, no renewal, no upsells. You believed early; we don&apos;t forget that.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Link to="/auth?mode=signup&plan=founders" data-testid="landing-pricing-cta" className="btn-primary inline-flex items-center justify-center gap-3">
                  {seats.remaining > 0 ? "Claim a founder seat" : "Founder seats sold out"} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/demo" data-testid="landing-pricing-demo" className="btn-secondary inline-flex items-center justify-center gap-3">
                  See it work first
                </Link>
              </div>

              <p className="text-[11px] tracking-[0.18em] uppercase text-foreground/45 font-mono">
                Once we hit {seats.cap} founders, the offer closes. Standard pricing takes over.
              </p>
            </div>

            {/* Monthly tier - visible but greyed out / "later" */}
            <div className="lg:col-span-5 brut-tile p-6 sm:p-8 md:p-12 relative opacity-60" data-testid="landing-monthly-tile">
              <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2 py-1 border border-foreground/25 text-[9px] uppercase tracking-[0.22em] font-mono text-foreground/55">
                <Lock className="w-2.5 h-2.5" /> After founders close
              </div>

              <div className="terminal-label mb-6 text-foreground/60">[ STANDARD.MONTHLY ]</div>
              <div className="flex items-baseline gap-2 mb-2">
                <h3 className="serif-mod text-5xl sm:text-6xl tracking-[-0.03em] leading-none" style={{ fontWeight: 400 }}>$30</h3>
                <span className="text-[11px] tracking-[0.22em] uppercase text-foreground/50 font-mono">/ month</span>
              </div>
              <p className="text-sm text-foreground/65 leading-relaxed mb-6">
                Standard subscription tier, available after the {seats.cap} founder seats are claimed. Cancel anytime.
              </p>
              <ul className="space-y-2.5 mb-6">
                {[
                  "Daily AI-ranked job feed (no cap)",
                  "Pre-generated tailored resumes + cover letters",
                  "Per-application portfolio pages",
                  "Inline editing of all materials",
                  "Sent kanban tracker",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[13px] text-foreground/70">
                    <span className="text-foreground/40 mt-0.5">▸</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-foreground/10">
                <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-foreground/50">
                  Free trial: sign up, build your profile, tailor 3 applications. Pay when you&apos;re ready for the rest.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter variant="site" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PortfolioReorderDemo
// Visualizes how the same 5 case-studies get re-ordered for two different jobs.
// Pieces share the same identity (color, label) across rows; only their
// position + the highlighted "boost" tag changes.
// ---------------------------------------------------------------------------
const DEMO_PIECES = [
  { id: "A", label: "Editorial / type system", tag: "BRAND",  color: "#C8392F" },
  { id: "B", label: "SaaS dashboard redesign",  tag: "UX",     color: "#1F2937" },
  { id: "C", label: "Album cover series",       tag: "ART DIR",color: "#D97706" },
  { id: "D", label: "DTC packaging refresh",    tag: "BRAND",  color: "#0F766E" },
  { id: "E", label: "Onboarding flow rebuild",  tag: "UX",     color: "#4338CA" },
];

const DEMO_LANES = [
  { jobLabel: "Brand Designer · Cultural institution",
    posting: "We're hiring a senior brand designer who lives in editorial systems, identity, and considered, idea-first work.",
    boost: "BRAND",
    order: ["A", "D", "C", "E", "B"] },
  { jobLabel: "Senior Product Designer · B2B SaaS",
    posting: "Looking for a product designer to lead onboarding, dashboard UX, and complex enterprise workflows.",
    boost: "UX",
    order: ["E", "B", "A", "D", "C"] },
];

function PortfolioReorderDemo() {
  const byId = Object.fromEntries(DEMO_PIECES.map((p) => [p.id, p]));
  return (
    <div className="mt-3 brut-tile p-5 sm:p-6 md:p-10" data-testid="portfolio-reorder-demo">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 sm:mb-8">
        <div>
          <div className="terminal-label mb-3">[ LIVE.DEMO ]</div>
          <h3 className="serif-mod text-xl sm:text-2xl md:text-3xl tracking-[-0.02em] max-w-2xl leading-[1.15]" style={{ fontWeight: 500 }}>
            Same portfolio. Two jobs. <span className="whimsy text-primary text-[1.1em]">Watch the order change.</span>
          </h3>
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-foreground/45 font-mono">
          Demo · No real applications shown
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {DEMO_LANES.map((lane, idx) => (
          <div
            key={lane.jobLabel}
            className="glass-tile p-4 sm:p-5 md:p-6"
            data-testid={`reorder-lane-${idx}`}
          >
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
              <div className="text-[10px] tracking-[0.22em] uppercase text-primary font-mono">Job · {lane.boost} priority</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-foreground/45 font-mono">Lane / {String(idx + 1).padStart(2, "0")}</div>
            </div>
            <div className="serif-mod text-base sm:text-lg md:text-xl mb-3 tracking-tight" style={{ fontWeight: 500 }}>
              {lane.jobLabel}
            </div>
            <p className="text-xs text-foreground/65 leading-relaxed mb-5 font-mono">
              &ldquo;{lane.posting}&rdquo;
            </p>

            {/* Tile strip */}
            <ol className="grid grid-cols-5 gap-1.5 sm:gap-2" aria-label="Portfolio order">
              {lane.order.map((pid, pos) => {
                const p = byId[pid];
                const boosted = p.tag === lane.boost;
                return (
                  <li
                    key={pid}
                    data-testid={`reorder-lane-${idx}-piece-${pos}`}
                    className={`group relative aspect-[3/4] flex flex-col justify-between p-1.5 sm:p-2 border transition-transform duration-300 ease-out ${boosted ? "border-primary" : "border-foreground/20"}`}
                    style={{
                      backgroundColor: p.color,
                      transform: boosted && pos === 0 ? "translateY(-4px)" : "none",
                    }}
                  >
                    <div className="flex items-start justify-between text-[7px] sm:text-[8px] tracking-[0.16em] uppercase font-mono" style={{ color: "rgba(255,255,255,0.85)" }}>
                      <span>{String(pos + 1).padStart(2, "0")}</span>
                      <span className="hidden sm:inline">{p.tag}</span>
                    </div>
                    <div className="text-[8px] sm:text-[9px] leading-tight font-mono" style={{ color: "rgba(255,255,255,0.95)" }}>
                      {p.label}
                    </div>
                    {boosted && (
                      <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-primary text-primary-foreground text-[7px] sm:text-[8px] tracking-[0.2em] uppercase px-1 sm:px-1.5 py-0.5 font-mono shadow">
                        Boosted
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>

            {/* Caption */}
            <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-foreground/55 font-mono">
              {lane.boost} work surfaces first · case study intros rewritten in the posting&apos;s language
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
